import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  OndemandVideoOutlined, ForumOutlined, GridViewOutlined, StorefrontOutlined,
  AddOutlined, DeleteOutlineOutlined, ChevronLeftOutlined, ChevronRightOutlined,
  SearchOutlined, EditOutlined, VideocamOutlined,
  ImageOutlined, CropOutlined, FitScreenOutlined,
  TextFieldsOutlined,
} from '@mui/icons-material';
import { PRIMARY, LOBBY_BREAKPOINTS, resolveHotspotStyleForBp, setHotspotStyleBpOverride } from '../constants';
import { ContentBrowserModal } from '../ContentBrowserModal';

const ExhibitorGridClipDef = () => (
  <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden', pointerEvents: 'none' }}>
    <defs>
      <clipPath id="lobby-booth-clip" clipPathUnits="objectBoundingBox">
        <path d="M0 0.9 Q0 1 0.1 1 L0.9 1 Q1 1 1 0.9 L0.88 0.1 Q0.88 0 0.78 0 L0.22 0 Q0.12 0 0.12 0.1 Z" />
      </clipPath>
    </defs>
  </svg>
);

export function ExhibitorGridElement({ hotspot, exhibitors = [], selected, selectedLobbyExhibitorId, onSelectLobbyExhibitor, onSelectGrid, onAddExhibitor, onDeleteExhibitor, onReorderExhibitors, onCreateExhibitorLobby, onGoToExhibitorLobby, onUpdateExhibitor, onAddSlideForExhibitor, canvasScale = 1, onMouseDown, onClick, bp, lobbies = [], lobbySlides = [], isCarouselLobby = false }) {
  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 40;
  const h = hotspot.meta?.height   ?? 22.5;

  const isNarrowBp  = bp === 'tablet-portrait' || bp === 'mobile';
  const rawTileSize = hotspot.meta?.gridTileSize ?? (isNarrowBp ? 450 : 250);
  const rawGap      = hotspot.meta?.gridGap      ?? (isNarrowBp ? 40  : 8);
  const cardTemplate  = hotspot.meta?.gridCardTemplate  ?? 'masked'; // 'flat' | 'masked'
  const displayMode   = hotspot.meta?.gridDisplayMode   ?? 'paged';  // 'paged' | 'scroll'
  const tileSize = rawTileSize > 0 ? Math.round(rawTileSize * canvasScale) : 0;
  const gap      = Math.round(rawGap * canvasScale);
  // Font size for exhibitor name — proportional to tile dimensions so it scales
  // with canvas zoom rather than the browser's root font size.
  // Footer is 20% of tile height; name fills ~38% of that.
  const effectiveTile = tileSize > 0 ? tileSize : 110; // fallback for auto layout
  const nameFontSize  = Math.round(effectiveTile * 0.076);
  const color    = '#0d7a5f';

  // Search bar scaled sizes
  const scaledSearchFontSize = Math.round(18   * canvasScale);
  const scaledSearchIcon     = Math.round(20.6 * canvasScale);
  const scaledSearchClear    = Math.round(18.75 * canvasScale);
  const scaledSearchPx       = Math.round(10   * canvasScale);
  const scaledSearchPy       = Math.round(6    * canvasScale);
  const scaledSearchRadius   = Math.round(20   * canvasScale);
  const scaledSearchMaxW     = Math.round(420  * canvasScale);
  // Pagination nav scaled sizes
  const scaledNavBtn         = Math.round(33   * canvasScale);
  const scaledNavIcon        = Math.round(24   * canvasScale);
  const scaledDotActive      = Math.round(27   * canvasScale);
  const scaledDot            = Math.round(10.5 * canvasScale);

  const containerRef  = useRef(null);
  const searchBarRef  = useRef(null);
  const paginationRef = useRef(null);
  const dragSrcRef    = useRef(null);

  const [cols, setCols]           = useState(4);
  const [rows, setRows]           = useState(2);
  const [page, setPage]           = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [search, setSearch]       = useState('');
  const [dragOverId, setDragOverId] = useState(null);
  const [localOrder, setLocalOrder] = useState(null);
  const [confirmBoothEx, setConfirmBoothEx] = useState(null);
  // Set-destination modal state
  const [destinationEx, setDestinationEx] = useState(null);
  const [destSearch, setDestSearch]        = useState('');
  // Page slide animation — { fromPage, toPage, direction: 'left'|'right', phase: 'enter'|'animate' }
  const [pageTransition, setPageTransition] = useState(null);
  const pageTransitionTimer = useRef(null);

  useEffect(() => {
    const compute = () => {
      if (!containerRef.current) return;
      const totalW  = containerRef.current.clientWidth;
      const totalH  = containerRef.current.clientHeight;
      const searchH = searchBarRef.current?.offsetHeight  ?? 0;
      const pagH    = paginationRef.current?.offsetHeight ?? 0;
      const availW  = totalW - gap * 2;
      const availH  = totalH - searchH - pagH - gap * 2.5;
      if (availW <= 0 || availH <= 0) return;
      const resolved = tileSize > 0 ? tileSize : Math.floor(Math.min(availW / 3, availH / 2));
      const newCols = Math.max(1, Math.floor((availW + gap) / (resolved + gap)));
      const newRows = Math.max(1, Math.floor((availH + gap) / (resolved + gap)));
      setCols(newCols);
      setRows(newRows);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [tileSize, gap]);

  useEffect(() => { setLocalOrder(null); }, [exhibitors]);

  const displayExhibitors = localOrder ?? exhibitors;
  const filteredExhibitors = search.trim()
    ? displayExhibitors.filter(ex => ex.name?.toLowerCase().includes(search.toLowerCase()))
    : displayExhibitors;

  const isScroll = displayMode === 'scroll';

  const perPage        = isScroll ? Infinity : cols * rows;
  const pages          = isScroll ? 1 : Math.max(1, Math.ceil((filteredExhibitors.length + 1) / perPage));
  const safePage       = isScroll ? 0 : Math.min(page, pages - 1);
  const pageExhibitors = isScroll ? filteredExhibitors : filteredExhibitors.slice(safePage * perPage, safePage * perPage + perPage);
  const showAddOnPage  = !search.trim() && (isScroll || pageExhibitors.length < perPage);

  const colPct = `calc(${100 / cols}% - ${gap * (cols - 1) / cols}px)`;
  const stop = e => e.stopPropagation();

  // ── Stale-link cleanup ───────────────────────────────────────────────────────
  // Runs whenever exhibitors, lobbies, or lobbySlides change.
  // Any exhibitor whose boothAction points to a deleted slide or lobby gets
  // its link cleared automatically.
  useEffect(() => {
    if (!onUpdateExhibitor) return;
    exhibitors.forEach(ex => {
      const action = ex.boothAction;
      if (!action?.type || !action?.targetId) return;
      let isStale = false;
      if (action.type === 'slide') {
        isStale = !lobbySlides.some(s => s.id === action.targetId);
      } else if (action.type === 'lobby') {
        isStale = !lobbies.some(l => String(l._id) === String(action.targetId));
      }
      if (isStale) {
        onUpdateExhibitor({ ...ex, boothAction: null, boothLobbyId: null });
      }
    });
  }, [exhibitors, lobbies, lobbySlides]); // eslint-disable-line 

  const handleTileClick = (e, exId) => { stop(e); onSelectGrid?.(); onSelectLobbyExhibitor?.(selectedLobbyExhibitorId === exId ? null : exId); };
  const handleDeleteClick = (e, exId) => { stop(e); onDeleteExhibitor?.(exId); if (selectedLobbyExhibitorId === exId) onSelectLobbyExhibitor?.(null); };
  const handleAddClick = (e) => { stop(e); onSelectGrid?.(); if (pageExhibitors.length >= perPage - 1) setPage(safePage + 1); onAddExhibitor?.(); };

  const navigatePage = (toPage) => {
    if (isScroll || toPage === safePage || pageTransition) return;
    const direction = toPage > safePage ? 'left' : 'right';
    if (pageTransitionTimer.current) clearTimeout(pageTransitionTimer.current);
    setPageTransition({ fromPage: safePage, toPage, direction, phase: 'enter' });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPageTransition(prev => prev ? { ...prev, phase: 'animate' } : null);
      });
    });
    pageTransitionTimer.current = setTimeout(() => {
      setPage(toPage);
      setPageTransition(null);
    }, 380);
  };

  const handleDragStart = (e, id) => { dragSrcRef.current = id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); };
  const handleDragEnter = (e, id) => {
    e.preventDefault();
    const src = dragSrcRef.current;
    if (!src || src === id) return;
    const base   = exhibitors;
    const srcIdx = base.findIndex(ex => ex._id === src);
    const tgtIdx = base.findIndex(ex => ex._id === id);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const reordered = [...base];
    const [moved]   = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);
    setLocalOrder(reordered);
    setDragOverId(id);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const srcId = dragSrcRef.current;
    if (!srcId) { setDragOverId(null); return; }
    const reordered = (localOrder ?? exhibitors).map((ex, i) => ({ ...ex, order: i }));
    onReorderExhibitors?.(reordered);
    setDragOverId(null);
    dragSrcRef.current = null;
  };
  const handleDragEnd = () => { setDragOverId(null); setLocalOrder(null); dragSrcRef.current = null; };

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: tileSize > 0 ? `repeat(${cols}, ${tileSize}px)` : `repeat(${cols}, ${colPct})`,
    gridAutoRows: tileSize > 0 ? `${tileSize}px` : colPct,
    gap: `${gap}px`,
    alignContent: isScroll ? 'start' : 'center',
    justifyContent: 'center',
    width: '100%',
    flex: isScroll ? undefined : 1,
    px: `${gap}px`,
    pb: `${gap}px`,
    boxSizing: 'border-box',
    overflow: isScroll ? 'visible' : 'hidden',
  };

  const tileTransition = 'transform 0.22s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.18s ease';

  // Appear animation for cards — simple scale from 0 to 1
  const scaleIn = keyframes`
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  `;

  // Returns animation sx for a card at a given index (staggered delay)
  const tileAppearSx = (i) => ({
    animation: `${scaleIn} 0.28s ease-out both`,
    animationDelay: `${i * 38}ms`,
  });

  const BoothTile = ({ ex }) => {
    const boothColor     = ex.boothBgColor || '#1a1a2e';
    const isTileSelected = selectedLobbyExhibitorId === ex._id;
    const isHov          = hoveredId === ex._id;
    const isDragOver     = dragOverId === ex._id;
    const isDragging     = dragSrcRef.current === ex._id;
    const hasBoothLobby  = !!ex.boothLobbyId;


    // Inline name editing with single/double-click discrimination
    const [editingName, setEditingName] = React.useState(false);
    const [nameDraft, setNameDraft]     = React.useState(ex.name || '');
    const clickTimerRef = React.useRef(null);
    React.useEffect(() => { setNameDraft(ex.name || ''); }, [ex.name]);
    const commitName = () => {
      setEditingName(false);
      const trimmed = nameDraft.trim();
      if (trimmed && trimmed !== ex.name) onUpdateExhibitor?.({ ...ex, name: trimmed });
    };
    // Single vs double click: second click within 180ms = enter edit mode
    const handleCardClick = (e) => {
      if (editingName) return;
      stop(e);
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
        if (isTileSelected) { setEditingName(true); setNameDraft(ex.name || ''); }
        return;
      }
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        onSelectGrid?.();
        onSelectLobbyExhibitor?.(selectedLobbyExhibitorId === ex._id ? null : ex._id);
      }, 180);
    };

    const tileOuter = {
      position: 'relative', aspectRatio: '1',
      ...(tileSize > 0 ? { width: tileSize, height: tileSize } : {}),
    };

    const tileInner = {
      width: '100%', height: '100%',
      position: 'relative', cursor: isDragging ? 'grabbing' : 'grab',
      outline: isTileSelected ? `2.5px solid ${color}` : isDragOver ? `2px dashed rgba(255,255,255,0.7)` : 'none',
      outlineOffset: 3, borderRadius: cardTemplate === 'flat' ? '10px' : 1,
      opacity: isDragging ? 0.35 : 1,
      transform: isDragOver ? 'scale(1.05)' : 'scale(1)',
      transition: 'transform 0.15s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s, outline 0.12s',
      willChange: 'transform, opacity',
      overflow: 'hidden',
      '&:hover': { outline: isTileSelected ? `2.5px solid ${color}` : `2px solid rgba(13,122,95,0.7)`, outlineOffset: 3 },
    };


    // Resolve destination info from boothAction (new) or boothLobbyId (legacy)
    const boothAction   = ex.boothAction;
    const destType      = boothAction?.type || (ex.boothLobbyId ? 'lobby' : null);
    const destTargetId  = boothAction?.targetId || ex.boothLobbyId || null;
    const hasDestSlide  = destType === 'slide' && destTargetId;
    const hasDestLobby  = destType === 'lobby' && destTargetId;
    const hasAnyDest    = hasDestSlide || hasDestLobby;

    // ── Flat template (light card) ────────────────────────────────────────────
    if (cardTemplate === 'flat') {
      return (
        <Box sx={tileOuter}>
          
          <Box
            draggable
            onMouseDown={e => e.stopPropagation()}
            onMouseEnter={() => setHoveredId(ex._id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={handleCardClick}
            onDragStart={e => handleDragStart(e, ex._id)}
            onDragEnter={e => handleDragEnter(e, ex._id)}
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, ex._id)}
            onDragEnd={handleDragEnd}
            sx={{ ...tileInner, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
          >
            {/* Thumbnail — top 80% */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '80%', bgcolor: boothColor, overflow: 'hidden' }}>
              {ex.boothThumbnail ? (
                <Box component="img" src={ex.boothThumbnail} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: ex.boothThumbnailFit || 'cover', display: 'block' }} />
              ) : (
                <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, rgba(16,117,186,0.2) 0%, ${boothColor} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StorefrontOutlined sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.18)' }} />
                </Box>
              )}
              {/* Create Slide hover button — carousel mode only */}
              {isCarouselLobby && isHov && !hasAnyDest && !isDragging && (
                <Box
                  onMouseDown={stop}
                  onClick={e => { stop(e); onAddSlideForExhibitor?.(ex); }}
                  sx={{
                    position: 'absolute', top: '5%', left: '5%', zIndex: 6,
                    display: 'flex', alignItems: 'center', gap: '3px',
                    px: '7px', py: '4px', borderRadius: '5px',
                    background: 'rgba(194,65,12,0.88)', backdropFilter: 'blur(4px)', cursor: 'pointer',
                    '&:hover': { background: 'rgba(194,65,12,1)' },
                  }}
                >
                  <Box sx={{ fontSize: 10, color: '#fff', lineHeight: 1, fontWeight: 700 }}>＋</Box>
                  <Typography sx={{ fontSize: nameFontSize * 0.82, color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                    New Slide
                  </Typography>
                </Box>
              )}

              {/* Create Lobby hover button — normal lobby mode only */}
              {!isCarouselLobby && isHov && !hasAnyDest && !isDragging && (
                <Box
                  onMouseDown={stop}
                  onClick={e => { stop(e); onCreateExhibitorLobby?.(ex); }}
                  sx={{
                    position: 'absolute', top: '5%', left: '5%', zIndex: 6,
                    display: 'flex', alignItems: 'center', gap: '3px',
                    px: '7px', py: '4px', borderRadius: '5px',
                    background: 'rgba(13,122,95,0.88)', backdropFilter: 'blur(4px)', cursor: 'pointer',
                    '&:hover': { background: 'rgba(13,122,95,1)' },
                  }}
                >
                  <Box sx={{ fontSize: 10, color: '#fff', lineHeight: 1, fontWeight: 700 }}>＋</Box>
                  <Typography sx={{ fontSize: nameFontSize * 0.82, color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                    New Lobby
                  </Typography>
                </Box>
              )}

              {/* Delete button — top right, hover only */}
              {isHov && !isDragging && (
                <Box
                  onMouseDown={stop}
                  onClick={e => { stop(e); handleDeleteClick(e, ex._id); }}
                  sx={{
                    position: 'absolute', top: '5%', right: '5%', zIndex: 7,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    background: 'rgba(220,38,38,0.82)', backdropFilter: 'blur(4px)', cursor: 'pointer',
                    '&:hover': { background: 'rgba(220,38,38,1)' },
                  }}
                >
                  <DeleteOutlineOutlined sx={{ fontSize: nameFontSize * 1.4, color: '#fff' }} />
                </Box>
              )}

              {/* Link indicator */}
              {hasAnyDest && (() => {
                const slideName = hasDestSlide
                  ? (lobbySlides.find(s => s.id === destTargetId)?.label || 'Slide')
                  : null;
                return (
                  <Box
                    onMouseDown={stop}
                    onClick={e => { stop(e); if (hasDestSlide) onGoToExhibitorLobby?.({ type: 'slide', targetId: destTargetId }); else onGoToExhibitorLobby?.(destTargetId); }}
                    sx={{
                      position: 'absolute', top: '5%', left: '5%', zIndex: 5,
                      display: 'flex', alignItems: 'center', gap: '3px',
                      px: '7px', py: '4px', borderRadius: '5px',
                      background: hasDestSlide ? 'rgba(194,65,12,0.88)' : 'rgba(16,117,186,0.88)',
                      backdropFilter: 'blur(4px)', cursor: 'pointer',
                      maxWidth: '80%', overflow: 'hidden',
                      '&:hover': { background: hasDestSlide ? 'rgba(194,65,12,1)' : 'rgba(16,117,186,1)' },
                    }}
                  >
                    <Box sx={{ fontSize: 12, color: '#fff', lineHeight: 1, flexShrink: 0 }}>🔗</Box>
                    <Typography sx={{ fontSize: nameFontSize * 0.93, color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {hasDestSlide ? slideName : 'Lobby'}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
            {/* White footer — bottom 20%, same as masked */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '20%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', px: '6%', overflow: 'hidden', borderTop: '1px solid #f0f4f8' }}>
              {ex.logoUrl ? (
                <Box component="img" src={ex.logoUrl} sx={{ maxHeight: '75%', maxWidth: '88%', objectFit: 'contain', display: 'block' }} />
              ) : (
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {editingName ? (
                    <Box component="input"
                      value={nameDraft}
                      onChange={e => setNameDraft(e.target.value)}
                      onBlur={commitName}
                      onKeyDown={e => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') { setNameDraft(ex.name || ''); setEditingName(false); } }}
                      onMouseDown={e => e.stopPropagation()}
                      autoFocus
                      sx={{ width: '100%', border: 'none', outline: `2px solid ${PRIMARY}`, borderRadius: '3px', background: '#fff', textAlign: 'center', fontFamily: 'inherit', fontWeight: 700, color: PRIMARY, fontSize: nameFontSize, px: '2px' }}
                    />
                  ) : (
                    <Box
                      sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'hidden',
                        '&:hover .edit-icon': { opacity: 1 },
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 700, color: PRIMARY, textAlign: 'center', lineHeight: 1, fontSize: nameFontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', userSelect: 'none' }}
                      >
                        {ex.name}
                      </Typography>
                      <Box
                        className="edit-icon"
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                          e.stopPropagation();
                          if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
                          setEditingName(true);
                          setNameDraft(ex.name || '');
                        }}
                        sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center', p: '2px', cursor: 'pointer', color: PRIMARY, '&:hover': { color: '#0a5a46' } }}
                      >
                        <EditOutlined sx={{ fontSize: nameFontSize * 1.5 }} />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    // ── Masked template (default) ─────────────────────────────────────────────
    return (
      <Box sx={tileOuter}>

        <Box
          draggable
          onMouseDown={e => e.stopPropagation()}
          onMouseEnter={() => setHoveredId(ex._id)}
          onMouseLeave={() => setHoveredId(null)}
          onClick={handleCardClick}
          onDragStart={e => handleDragStart(e, ex._id)}
          onDragEnter={e => handleDragEnter(e, ex._id)}
          onDragOver={handleDragOver}
          onDrop={e => handleDrop(e, ex._id)}
          onDragEnd={handleDragEnd}
          sx={tileInner}
        >
          <Box sx={{ width: '100%', height: '100%', clipPath: 'url(#lobby-booth-clip)', overflow: 'hidden' }}>
            <Box sx={{ position: 'relative', width: '100%', height: '80%', bgcolor: boothColor, overflow: 'hidden' }}>
              {ex.boothThumbnail ? (
                <Box component="img" src={ex.boothThumbnail} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: ex.boothThumbnailFit || 'cover', display: 'block' }} />
              ) : (
                <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, rgba(16,117,186,0.2) 0%, ${boothColor} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StorefrontOutlined sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.18)' }} />
                </Box>
              )}
              {/* Create Slide hover button — carousel mode only */}
              {isCarouselLobby && isHov && !hasAnyDest && !isDragging && (
                <Box
                  onMouseDown={stop}
                  onClick={e => { stop(e); onAddSlideForExhibitor?.(ex); }}
                  sx={{
                    position: 'absolute', top: '5%', left: '5%', zIndex: 6,
                    display: 'flex', alignItems: 'center', gap: '3px',
                    px: '7px', py: '4px', borderRadius: '5px',
                    background: 'rgba(194,65,12,0.88)', backdropFilter: 'blur(4px)', cursor: 'pointer',
                    '&:hover': { background: 'rgba(194,65,12,1)' },
                  }}
                >
                  <Box sx={{ fontSize: 10, color: '#fff', lineHeight: 1, fontWeight: 700 }}>＋</Box>
                  <Typography sx={{ fontSize: nameFontSize * 0.82, color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                    New Slide
                  </Typography>
                </Box>
              )}

              {/* Create Lobby hover button — normal lobby mode only */}
              {!isCarouselLobby && isHov && !hasAnyDest && !isDragging && (
                <Box
                  onMouseDown={stop}
                  onClick={e => { stop(e); onCreateExhibitorLobby?.(ex); }}
                  sx={{
                    position: 'absolute', top: '5%', left: '5%', zIndex: 6,
                    display: 'flex', alignItems: 'center', gap: '3px',
                    px: '7px', py: '4px', borderRadius: '5px',
                    background: 'rgba(13,122,95,0.88)', backdropFilter: 'blur(4px)', cursor: 'pointer',
                    '&:hover': { background: 'rgba(13,122,95,1)' },
                  }}
                >
                  <Box sx={{ fontSize: 10, color: '#fff', lineHeight: 1, fontWeight: 700 }}>＋</Box>
                  <Typography sx={{ fontSize: nameFontSize * 0.82, color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                    New Lobby
                  </Typography>
                </Box>
              )}

              {/* Delete button — top right, hover only */}
              {isHov && !isDragging && (
                <Box
                  onMouseDown={stop}
                  onClick={e => { stop(e); handleDeleteClick(e, ex._id); }}
                  sx={{
                    position: 'absolute', top: '5%', right: '5%', zIndex: 7,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    background: 'rgba(220,38,38,0.82)', backdropFilter: 'blur(4px)', cursor: 'pointer',
                    '&:hover': { background: 'rgba(220,38,38,1)' },
                  }}
                >
                  <DeleteOutlineOutlined sx={{ fontSize: nameFontSize * 1.4, color: '#fff' }} />
                </Box>
              )}

              {/* Link indicator */}
              {hasAnyDest && (() => {
                const slideName = hasDestSlide
                  ? (lobbySlides.find(s => s.id === destTargetId)?.label || 'Slide')
                  : null;
                return (
                  <Box
                    onMouseDown={stop}
                    onClick={e => { stop(e); if (hasDestSlide) onGoToExhibitorLobby?.({ type: 'slide', targetId: destTargetId }); else onGoToExhibitorLobby?.(destTargetId); }}
                    sx={{
                      position: 'absolute', top: '5%', left: '5%', zIndex: 5,
                      display: 'flex', alignItems: 'center', gap: '3px',
                      px: '7px', py: '4px', borderRadius: '5px',
                      background: hasDestSlide ? 'rgba(194,65,12,0.88)' : 'rgba(16,117,186,0.88)',
                      backdropFilter: 'blur(4px)', cursor: 'pointer',
                      maxWidth: '80%', overflow: 'hidden',
                      '&:hover': { background: hasDestSlide ? 'rgba(194,65,12,1)' : 'rgba(16,117,186,1)' },
                    }}
                  >
                    <Box sx={{ fontSize: 12, color: '#fff', lineHeight: 1, flexShrink: 0 }}>🔗</Box>
                    <Typography sx={{ fontSize: nameFontSize * 0.93, color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {hasDestSlide ? slideName : 'Lobby'}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
            <Box sx={{ width: '100%', height: '20%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', px: '6%', overflow: 'hidden' }}>
              {ex.logoUrl ? (
                <Box component="img" src={ex.logoUrl} sx={{ maxHeight: '75%', maxWidth: '88%', objectFit: 'contain', display: 'block' }} />
              ) : (
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {editingName ? (
                    <Box component="input"
                      value={nameDraft}
                      onChange={e => setNameDraft(e.target.value)}
                      onBlur={commitName}
                      onKeyDown={e => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') { setNameDraft(ex.name || ''); setEditingName(false); } }}
                      onMouseDown={e => e.stopPropagation()}
                      autoFocus
                      sx={{ width: '100%', border: 'none', outline: `2px solid ${PRIMARY}`, borderRadius: '3px', background: '#fff', textAlign: 'center', fontFamily: 'inherit', fontWeight: 700, color: PRIMARY, fontSize: nameFontSize, px: '2px' }}
                    />
                  ) : (
                    <Box
                      sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'hidden',
                        '&:hover .edit-icon': { opacity: 1 },
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 700, color: PRIMARY, textAlign: 'center', lineHeight: 1, fontSize: nameFontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', userSelect: 'none' }}
                      >
                        {ex.name}
                      </Typography>
                      <Box
                        className="edit-icon"
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                          e.stopPropagation();
                          if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
                          setEditingName(true);
                          setNameDraft(ex.name || '');
                        }}
                        sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center', p: '2px', cursor: 'pointer', color: PRIMARY, '&:hover': { color: '#0a5a46' } }}
                      >
                        <EditOutlined sx={{ fontSize: nameFontSize * 1.5 }} />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const addTile = (index = 0, animate = true) => (
    <Box
      key={`add-${page}`}
      onMouseDown={stop} onClick={handleAddClick}
      sx={{
        aspectRatio: '1', ...(tileSize > 0 ? { width: tileSize, height: tileSize } : {}),
        borderRadius: cardTemplate === 'flat' ? '10px' : '4px',
        overflow: 'hidden', cursor: 'pointer',
        border: '1.5px dashed rgba(255,255,255,0.5)',
        background: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4%',
        transition: 'all 0.15s',
        '&:hover': { background: 'rgba(255,255,255,0.22)', borderColor: 'rgba(255,255,255,0.85)', boxShadow: '0 4px 24px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.3)' },
        ...(animate ? tileAppearSx(index) : {}),
      }}
    >
      <AddOutlined sx={{ fontSize: '3rem', color: '#fff', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }} />
      <Typography sx={{ fontSize: 'clamp(7.5px, 2.7cqw, 16.5px)', fontWeight: 600, color: 'rgba(255,255,255,0.9)', textAlign: 'center', lineHeight: 1.2, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Add Exhibitor</Typography>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      onMouseDown={e => { stop(e); onMouseDown?.(e); }}
      onClick={e => { stop(e); onClick?.(e); }}
      sx={{
        position: 'absolute',
        left: `${x}%`, top: `${y}%`,
        width: `${w}%`, height: `${h}%`,
        zIndex: 5 + (hotspot.order ?? 0),
        cursor: selected ? 'default' : 'grab',
        outline: selected ? `2px solid ${color}` : `1px dashed ${alpha(color, 0.4)}`,
        outlineOffset: selected ? 2 : 0,
        overflow: 'hidden', borderRadius: '4px',
        background: 'transparent',
        boxShadow: selected ? `0 0 0 3px rgba(13,122,95,0.2)` : 'none',
        pointerEvents: 'all',
        display: 'flex', flexDirection: 'column',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      <ExhibitorGridClipDef />

      <Box ref={searchBarRef} onMouseDown={e => { onMouseDown?.(e); }} sx={{ flexShrink: 0, px: `${gap}px`, pt: `${gap}px`, pb: `${gap / 2}px`, display: 'flex', justifyContent: 'center', cursor: 'grab' }}>
        <Box onMouseDown={stop} sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', maxWidth: `${scaledSearchMaxW}px`,
          background: 'rgba(10,14,26,0.72)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderRadius: `${scaledSearchRadius}px`,
          px: `${scaledSearchPx}px`, py: `${scaledSearchPy}px`,
          border: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
          '&:focus-within': { borderColor: 'rgba(255,255,255,0.55)', background: 'rgba(10,14,26,0.85)' },
        }}>
          <SearchOutlined sx={{ fontSize: scaledSearchIcon, color: '#ffffff', flexShrink: 0, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }} />
          <Box component="input" value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            onMouseDown={stop} placeholder="Search exhibitors…"
            sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: scaledSearchFontSize, color: '#ffffff', fontFamily: 'inherit', textShadow: '0 1px 3px rgba(0,0,0,0.7)', '&::placeholder': { color: 'rgba(255,255,255,0.55)' } }}
          />
          {search && (
            <Box onMouseDown={stop} onClick={e => { stop(e); setSearch(''); setPage(0); }}
              sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: scaledSearchClear, lineHeight: 1, '&:hover': { color: '#fff' } }}>✕</Box>
          )}
        </Box>
      </Box>

      {isScroll ? (
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.3)', borderRadius: 2 } }}>
          <Box sx={{ ...gridSx, pt: `${gap}px` }}>
            {pageExhibitors.map((ex, i) => (
              <Box key={ex._id} sx={{ transition: tileTransition, willChange: 'transform', ...tileAppearSx(i) }}>
                <BoothTile ex={ex} />
              </Box>
            ))}
            {showAddOnPage && addTile(pageExhibitors.length)}
          </Box>
        </Box>
      ) : (
        /* ── Carousel paged mode with sliding page transition ── */
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
          {(() => {
            // Render the grid content for any given page index.
            // `animate` — only true for the incoming page so only new cards scale-in.
            const renderPage = (pageIdx, animate = false) => {
              const pEx = filteredExhibitors.slice(pageIdx * perPage, pageIdx * perPage + perPage);
              const showAdd = !search.trim() && pEx.length < perPage;
              return (
                <Box sx={{ ...gridSx, pt: `${gap}px`, pb: pages > 1 ? `${gap / 2}px` : `${gap}px`, height: '100%', boxSizing: 'border-box' }}>
                  {pEx.map((ex, i) => (
                    <Box key={ex._id} sx={{ transition: tileTransition, willChange: 'transform', ...(animate ? tileAppearSx(i) : {}) }}>
                      <BoothTile ex={ex} />
                    </Box>
                  ))}
                  {showAdd && addTile(pEx.length, animate)}
                </Box>
              );
            };

            if (pageTransition) {
              const isLeft    = pageTransition.direction === 'left';
              const restX     = isLeft ? '0%'   : '-50%';
              const animateX  = isLeft ? '-50%'  : '0%';
              const translateX = pageTransition.phase === 'animate' ? animateX : restX;
              const leftPage  = isLeft ? pageTransition.fromPage : pageTransition.toPage;
              const rightPage = isLeft ? pageTransition.toPage   : pageTransition.fromPage;
              // Only the incoming page gets the appear animation; current page just slides out
              const leftAnimate  = !isLeft;
              const rightAnimate = isLeft;
              return (
                <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                  <Box sx={{
                    display: 'flex', position: 'absolute', inset: 0, width: '200%',
                    transform: `translateX(${translateX})`,
                    transition: pageTransition.phase === 'animate' ? 'transform 0.38s cubic-bezier(0.4,0,0.2,1)' : 'none',
                  }}>
                    <Box sx={{ width: '50%', height: '100%', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                      {renderPage(leftPage, leftAnimate)}
                    </Box>
                    <Box sx={{ width: '50%', height: '100%', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                      {renderPage(rightPage, rightAnimate)}
                    </Box>
                  </Box>
                </Box>
              );
            }

            return (
              <Box sx={{ position: 'absolute', inset: 0 }}>
                {renderPage(safePage)}
              </Box>
            );
          })()}
        </Box>
      )}

      {!isScroll && pages > 1 && (
        <Box ref={paginationRef} onMouseDown={stop} sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, pb: `${gap}px` }}>
          {/* Left arrow — high-contrast frosted */}
          <Box onClick={e => { stop(e); navigatePage(safePage - 1); }}
            sx={{
              width: scaledNavBtn, height: scaledNavBtn, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: safePage > 0 ? 'rgba(10,14,26,0.72)' : 'rgba(10,14,26,0.35)',
              backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.18)',
              boxShadow: safePage > 0 ? '0 2px 8px rgba(0,0,0,0.35)' : 'none',
              cursor: safePage > 0 ? 'pointer' : 'default',
              pointerEvents: safePage > 0 ? 'auto' : 'none',
              color: safePage > 0 ? '#fff' : 'rgba(255,255,255,0.3)',
              transition: 'all 0.15s',
              '&:hover': safePage > 0 ? { background: color, borderColor: color, boxShadow: `0 2px 12px ${color}55` } : {},
            }}>
            <ChevronLeftOutlined sx={{ fontSize: scaledNavIcon, filter: safePage > 0 ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' : 'none' }} />
          </Box>

          {/* Dot indicators */}
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            {Array.from({ length: pages }).map((_, i) => {
              const isActive = pageTransition ? i === pageTransition.toPage : i === safePage;
              return (
                <Box key={i} onClick={e => { stop(e); navigatePage(i); }}
                  sx={{ width: isActive ? scaledDotActive : scaledDot, height: scaledDot, borderRadius: 99, background: isActive ? '#fff' : 'rgba(10,14,26,0.55)', border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)', cursor: 'pointer', transition: 'all 0.25s', boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.4)' : 'none' }} />
              );
            })}
          </Box>

          {/* Right arrow — high-contrast frosted */}
          <Box onClick={e => { stop(e); navigatePage(safePage + 1); }}
            sx={{
              width: scaledNavBtn, height: scaledNavBtn, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: safePage < pages - 1 ? 'rgba(10,14,26,0.72)' : 'rgba(10,14,26,0.35)',
              backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.18)',
              boxShadow: safePage < pages - 1 ? '0 2px 8px rgba(0,0,0,0.35)' : 'none',
              cursor: safePage < pages - 1 ? 'pointer' : 'default',
              pointerEvents: safePage < pages - 1 ? 'auto' : 'none',
              color: safePage < pages - 1 ? '#fff' : 'rgba(255,255,255,0.3)',
              transition: 'all 0.15s',
              '&:hover': safePage < pages - 1 ? { background: color, borderColor: color, boxShadow: `0 2px 12px ${color}55` } : {},
            }}>
            <ChevronRightOutlined sx={{ fontSize: scaledNavIcon, filter: safePage < pages - 1 ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' : 'none' }} />
          </Box>
        </Box>
      )}

      {/* ── Set Destination modal ── */}
      {destinationEx && (
        <Box onMouseDown={stop} onClick={e => { stop(e); setDestinationEx(null); }}
          sx={{ position: 'absolute', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
        >
          <Box onClick={stop} sx={{ background: '#1a1d2e', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', width: '88%', maxWidth: 320, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '85%' }}>
            {/* Header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#fff' }}>
                Set Destination — {destinationEx.name}
              </Typography>
              <Box onClick={() => setDestinationEx(null)} sx={{ color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: 16, lineHeight: 1, '&:hover': { color: '#fff' } }}>✕</Box>
            </Box>

            {/* Search */}
            <Box sx={{ px: 1.5, pt: 1.25, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.6, borderRadius: '8px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <SearchOutlined sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }} />
                <Box component="input" value={destSearch} onChange={e => setDestSearch(e.target.value)} placeholder="Search…"
                  sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: '#fff', fontSize: '0.72rem', fontFamily: 'inherit', '&::placeholder': { color: 'rgba(255,255,255,0.35)' } }} />
              </Box>
            </Box>

            {/* Destination list */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

              {/* Slides section — only for carousel lobbies */}
              {isCarouselLobby && lobbySlides.length > 0 && (() => {
                const filtered = lobbySlides.filter(s => !destSearch.trim() || s.label?.toLowerCase().includes(destSearch.toLowerCase()));
                if (filtered.length === 0) return null;
                return (
                  <>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: color, textTransform: 'uppercase', letterSpacing: '0.08em', px: 0.5, mb: 0.25 }}>Slides</Typography>
                    {filtered.map((slide, idx) => {
                      const isSelected = destinationEx.boothAction?.type === 'slide' && destinationEx.boothAction?.targetId === slide.id;
                      return (
                        <Box key={slide.id} onClick={() => {
                          const updated = { ...destinationEx, boothAction: { type: 'slide', targetId: slide.id } };
                          onUpdateExhibitor?.(updated);
                          setDestinationEx(null);
                        }}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer', background: isSelected ? alpha(color, 0.25) : 'transparent', border: `1px solid ${isSelected ? color : 'transparent'}`, '&:hover': { background: alpha(color, 0.15) } }}
                        >
                          <Box sx={{ width: 28, height: 18, borderRadius: '3px', flexShrink: 0, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.15)', background: slide.bgUrl ? 'transparent' : 'rgba(255,255,255,0.05)', position: 'relative' }}>
                            {slide.bgUrl && <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${slide.bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ fontSize: '0.45rem', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{idx + 1}</Typography>
                            </Box>
                          </Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: isSelected ? 700 : 400, color: isSelected ? '#6ee7b7' : 'rgba(255,255,255,0.85)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {slide.label || `Slide ${idx + 1}`}
                          </Typography>
                          {isSelected && <Box sx={{ fontSize: 11, color: '#6ee7b7' }}>✓</Box>}
                        </Box>
                      );
                    })}
                  </>
                );
              })()}

              {/* Lobbies section */}
              {(() => {
                const otherLobbies = lobbies.filter(l => l._id !== hotspot.hotspotAction?.lobbyId && l.lobbyType !== 'booth');
                const filtered = otherLobbies.filter(l => !destSearch.trim() || l.name?.toLowerCase().includes(destSearch.toLowerCase()));
                if (filtered.length === 0 && !isCarouselLobby) return (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>No lobbies found</Typography>
                  </Box>
                );
                if (filtered.length === 0) return null;
                return (
                  <>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.08em', px: 0.5, mb: 0.25, mt: isCarouselLobby ? 0.75 : 0 }}>Lobbies</Typography>
                    {filtered.map(l => {
                      const isSelected = destinationEx.boothAction?.type === 'lobby' && destinationEx.boothAction?.targetId === l._id;
                      const isLegacySelected = !destinationEx.boothAction && destinationEx.boothLobbyId === l._id;
                      const sel = isSelected || isLegacySelected;
                      return (
                        <Box key={l._id} onClick={() => {
                          const updated = { ...destinationEx, boothAction: { type: 'lobby', targetId: l._id }, boothLobbyId: l._id };
                          onUpdateExhibitor?.(updated);
                          setDestinationEx(null);
                        }}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer', background: sel ? alpha('#1075BA', 0.25) : 'transparent', border: `1px solid ${sel ? '#1075BA' : 'transparent'}`, '&:hover': { background: alpha('#1075BA', 0.15) } }}
                        >
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: l.lobbyType === 'carousel' ? '#c2410c' : l.lobbyType === 'plenary' ? '#8b4fcc' : '#1075BA' }} />
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: sel ? 700 : 400, color: sel ? '#93c5fd' : 'rgba(255,255,255,0.85)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.name}
                          </Typography>
                          {sel && <Box sx={{ fontSize: 11, color: '#93c5fd' }}>✓</Box>}
                        </Box>
                      );
                    })}
                  </>
                );
              })()}

              {/* Clear destination */}
              {(destinationEx.boothAction || destinationEx.boothLobbyId) && (
                <Box onClick={() => {
                  const updated = { ...destinationEx, boothAction: null, boothLobbyId: null };
                  onUpdateExhibitor?.(updated);
                  setDestinationEx(null);
                }}
                  sx={{ mt: 0.5, px: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(220,38,38,0.35)', '&:hover': { background: 'rgba(220,38,38,0.12)' } }}
                >
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(220,38,38,0.8)', fontWeight: 600, textAlign: 'center' }}>Clear Destination</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {confirmBoothEx && (
        <Box onMouseDown={stop} onClick={stop}
          sx={{ position: 'absolute', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
        >
          <Box sx={{ background: '#1a1d2e', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', p: 2.5, width: '80%', maxWidth: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#fff' }}>Create Exhibitor Lobby</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
              This action will create a new lobby for <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>{confirmBoothEx.name}</Box>. The lobby will be linked to this exhibitor.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Box onClick={() => setConfirmBoothEx(null)}
                sx={{ flex: 1, py: 0.9, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', fontWeight: 600, transition: 'all 0.15s', '&:hover': { borderColor: 'rgba(255,255,255,0.4)', color: '#fff' } }}>
                Cancel
              </Box>
              <Box onClick={async () => { const ex = confirmBoothEx; setConfirmBoothEx(null); await onCreateExhibitorLobby?.(ex); }}
                sx={{ flex: 1.5, py: 0.9, borderRadius: '8px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '0.72rem', fontWeight: 600, transition: 'all 0.15s', '&:hover': { background: '#0a6b4f' } }}>
                Create Exhibitor Lobby
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── TextElement ───────────────────────────────────────────────────────────────
const TEXT_HANDLES = ['nw','n','ne','e','se','s','sw','w'];
const HANDLE_CURSORS = { nw:'nw-resize', n:'n-resize', ne:'ne-resize', e:'e-resize', se:'se-resize', s:'s-resize', sw:'sw-resize', w:'w-resize' };
const HANDLE_POS = {
  nw: { top: -5, left: -5 },
  n:  { top: -5, left: '50%', marginLeft: '-4px' },
  ne: { top: -5, right: -5 },
  e:  { top: '50%', right: -5, marginTop: '-4px' },
  se: { bottom: -5, right: -5 },
  s:  { bottom: -5, left: '50%', marginLeft: '-4px' },
  sw: { bottom: -5, left: -5 },
  w:  { top: '50%', left: -5, marginTop: '-4px' },
};

export function TextElement({ hotspot, selected, previewMode, canvasScale = 1, onMouseDown, onClick, onUpdate, onHandleMouseDown, onContextMenu, bp = 'desktop' }) {
  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width  ?? 35;
  const h = hotspot.meta?.height ?? 12;

  const text          = hotspot.meta?.text          ?? 'Text';

  // Resolve font props with bp override fallback
  const bpTextStyle   = (bp && bp !== 'desktop') ? (hotspot?.responsive?.[bp]?.textStyle || {}) : {};
  const fontSize      = ((bpTextStyle.fontSize      ?? hotspot.meta?.fontSize      ?? 25)) * canvasScale;
  const fontWeight    =  (bpTextStyle.fontWeight    ?? hotspot.meta?.fontWeight    ?? 600);
  const color         =  (bpTextStyle.color         ?? hotspot.meta?.color         ?? '#ffffff');
  const textAlign     =  (bpTextStyle.textAlign     ?? hotspot.meta?.textAlign     ?? 'center');

  const bgColor       = hotspot.meta?.bgColor        ?? 'rgba(0,0,0,0.7)';
  const bgStrokeColor = hotspot.meta?.bgStrokeColor  ?? '#ffffff';
  const bgStrokeWidth = (hotspot.meta?.bgStrokeWidth ?? 3) * canvasScale;
  const borderRadius  = (hotspot.meta?.borderRadius  ?? 6) * canvasScale;

  const [editing, setEditing]   = useState(false);
  const [draft,   setDraft]     = useState(text);
  const textareaRef             = useRef(null);

  useEffect(() => { setDraft(text); }, [text]);

  const handleDoubleClick = (e) => {
    if (previewMode) return;
    e.stopPropagation();
    setEditing(true);
    setTimeout(() => { textareaRef.current?.focus(); textareaRef.current?.select(); }, 20);
  };

  const commitEdit = () => {
    setEditing(false);
    if (draft !== text) onUpdate?.({ ...hotspot, meta: { ...hotspot.meta, text: draft } });
  };

  return (
    <Box
      onMouseDown={editing ? undefined : (e => { e.stopPropagation(); onMouseDown?.(e); })}
      onClick={editing ? undefined : onClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={!previewMode && onContextMenu ? e => { e.preventDefault(); e.stopPropagation(); onContextMenu(e); } : undefined}
      sx={{
        position: 'absolute',
        left: `${x}%`, top: `${y}%`,
        width: `${w}%`, height: `${h}%`,
        zIndex: 5 + (hotspot.order ?? 0),
        cursor: selected && !previewMode ? 'move' : previewMode ? 'default' : 'grab',
        outline: selected && !previewMode ? '2px solid #7c3aed' : !previewMode ? '1px dashed rgba(124,58,237,0.35)' : 'none',
        outlineOffset: selected ? 2 : 0,
        boxShadow: selected && !previewMode ? '0 0 0 3px rgba(124,58,237,0.2)' : 'none',
        background: bgColor,
        border: bgStrokeWidth > 0 ? `${bgStrokeWidth}px solid ${bgStrokeColor}` : 'none',
        borderRadius: `${borderRadius}px`,
        display: 'flex', alignItems: 'center', justifyContent:
          textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
        overflow: 'visible',
        pointerEvents: 'all',
        userSelect: 'none', WebkitUserSelect: 'none',
        boxSizing: 'border-box',
        px: '6px',
        transition: 'outline 0.1s, box-shadow 0.1s',
      }}
    >
      {/* HTML resize handles */}
      {selected && !previewMode && TEXT_HANDLES.map(handle => (
        <Box
          key={handle}
          onMouseDown={e => { e.stopPropagation(); onHandleMouseDown?.(e, handle); }}
          sx={{
            position: 'absolute',
            width: 8, height: 8,
            borderRadius: '2px',
            background: '#fff',
            border: '1.5px solid #7c3aed',
            cursor: HANDLE_CURSORS[handle],
            zIndex: 30,
            ...HANDLE_POS[handle],
          }}
        />
      ))}

      {/* Text content */}
      <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center', containerType: 'size' }}>
        {editing ? (
          <Box
            component="textarea"
            ref={textareaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => {
              if (e.key === 'Escape') { setDraft(text); setEditing(false); }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
            }}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            sx={{
              width: '100%', height: '100%',
              background: 'transparent', border: 'none', outline: 'none', resize: 'none',
              color, fontWeight,
              fontSize: `${fontSize}px`,
              textAlign, fontFamily: 'inherit',
              cursor: 'text', userSelect: 'text', WebkitUserSelect: 'text',
              lineHeight: 1.3,
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              color, fontWeight,
              fontSize: `${fontSize}px`,
              textAlign,
              lineHeight: 1.3,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              userSelect: 'none',
            }}
          >
            {text}
          </Box>
        )}
      </Box>
    </Box>
  );
}


// ── Lazy-load hls.js from CDN (shared singleton) ────────────────────────────
let _hlsPromise = null;
function loadHls() {
  if (_hlsPromise) return _hlsPromise;
  _hlsPromise = new Promise((resolve) => {
    if (window.Hls) return resolve(window.Hls);
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.min.js';
    script.onload = () => resolve(window.Hls);
    document.head.appendChild(script);
  });
  return _hlsPromise;
}

// ── Mux live video player (canvas builder) ───────────────────────────────────
function CanvasMuxPlayer({ playbackId, muxStatus, label }) {
  const videoRef = useRef(null);
  const hlsRef   = useRef(null);
  const isLive   = muxStatus === 'active';

  useEffect(() => {
    // Only attempt HLS load when the stream is actually live
    if (!playbackId || !isLive || !videoRef.current) return;

    const src = `https://stream.mux.com/${playbackId}.m3u8`;

    loadHls().then(Hls => {
      // Tear down any previous instance before creating a new one
      hlsRef.current?.destroy();
      hlsRef.current = null;

      if (Hls.isSupported()) {
        const hls = new Hls({ lowLatencyMode: true });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(videoRef.current);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          videoRef.current?.play().catch(() => {});
        });
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = src;
        videoRef.current.play().catch(() => {});
      }
    });

    return () => { hlsRef.current?.destroy(); hlsRef.current = null; };
  }, [playbackId, isLive]); // re-runs when stream goes live

  if (!isLive) {
    return (
      <Box sx={{
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#0f0f0f', gap: 0.75, pointerEvents: 'none',
      }}>
       
        <Box sx={{ fontSize: '1rem', color: '#64748b', textAlign: 'center', px: 1, pointerEvents: 'none' }}>
          {muxStatus === 'idle' ? 'Awaiting stream' : muxStatus === 'disabled' ? 'Stream ended' : 'Awaiting stream'}<br />
          {/* <span style={{ fontSize: '0.55rem', color: '#475569' }}>{label || 'Mux Livestream'}</span> */}
        </Box>
      </Box>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay playsInline muted controls
      style={{ width: '100%', height: '100%', display: 'block', background: '#000', pointerEvents: 'none' }}
    />
  );
}

export function StreamElement({ hotspot, selected, previewMode, onMouseDown, onClick }) {
  const isChat     = hotspot.hotspotType === 'livestream-chat';
  const side       = hotspot.meta?.side || 'right';
  const action     = hotspot.hotspotAction || {};
  const streamType = action.streamType || 'iframe';
  const isMuxLive  = !isChat && streamType === 'mux';

  const x = isChat ? (side === 'left' ? 0 : 100 - (hotspot.meta?.width ?? 22)) : (hotspot.points?.[0]?.x ?? 10);
  const y = isChat ? 0 : (hotspot.points?.[0]?.y ?? 10);
  const w = hotspot.meta?.width  ?? (isChat ? 22 : 30);
  const h = isChat ? 100 : (hotspot.meta?.height ?? 16.875);

  const color   = isChat ? '#0a7aff' : '#EF4444';
  const outline = selected
    ? `2px solid ${color}`
    : `1px dashed ${isChat ? 'rgba(10,122,255,0.4)' : 'rgba(239,68,68,0.4)'}`;

  return (
    <Box
      onMouseDown={isChat ? (e => { e.stopPropagation(); onClick?.(e); }) : onMouseDown}
      onClick={onClick}
      sx={{
        position: 'absolute',
        left: `${x}%`, top: `${y}%`,
        width: `${w}%`, height: `${h}%`,
        zIndex: 5 + (hotspot.order ?? 0),
        cursor: isChat ? 'pointer' : (selected ? 'move' : 'grab'),
        outline, outlineOffset: selected ? 2 : 0,
        overflow: 'hidden', borderRadius: '4px',
        background: isMuxLive ? '#000' : isChat ? 'rgba(10,14,26,0.7)' : 'rgba(10,14,26,0.55)',
        boxShadow: selected ? `0 0 0 3px ${isChat ? 'rgba(10,122,255,0.25)' : 'rgba(239,68,68,0.25)'}` : 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'all',
      }}
    >
      {isChat ? (
        <ForumOutlined sx={{ fontSize: 32, color: 'rgba(10,122,255,0.6)', pointerEvents: 'none' }} />
      ) : isMuxLive ? (
        <CanvasMuxPlayer playbackId={action.muxPlaybackId || null} muxStatus={action.muxStatus || null} label={hotspot.label} />
      ) : (
        <OndemandVideoOutlined sx={{ fontSize: 32, color: 'rgba(239,68,68,0.6)', pointerEvents: 'none' }} />
      )}

      {/* "Mux Livestream" label badge when selected in builder */}
      {selected && !previewMode && isMuxLive && (
        <Box sx={{
          position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.65)', color: '#fff',
          px: 1, py: 0.3, borderRadius: '10px',
          fontSize: '0.6rem', fontWeight: 600, whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
          Livestream
        </Box>
      )}
    </Box>
  );
}

// ── ImageElement ──────────────────────────────────────────────────────────────
const IMAGE_HANDLES = ['nw','n','ne','e','se','s','sw','w'];
const IMAGE_HANDLE_CURSORS = { nw:'nw-resize', n:'n-resize', ne:'ne-resize', e:'e-resize', se:'se-resize', s:'s-resize', sw:'sw-resize', w:'w-resize' };
const IMAGE_HANDLE_POS = {
  nw: { top: -5, left: -5 }, n:  { top: -5, left: '50%', marginLeft: '-4px' },
  ne: { top: -5, right: -5 }, e:  { top: '50%', right: -5, marginTop: '-4px' },
  se: { bottom: -5, right: -5 }, s: { bottom: -5, left: '50%', marginLeft: '-4px' },
  sw: { bottom: -5, left: -5 }, w: { top: '50%', left: -5, marginTop: '-4px' },
};

export function ImageElement({ hotspot, selected, previewMode, canvasScale = 1, onMouseDown, onClick, onHandleMouseDown, onOpenImagePopover, onContextMenu }) {
  const elemRef = useRef(null);
  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 25;
  const h = hotspot.meta?.height   ?? 14;

  const imageUrl      = hotspot.meta?.imageUrl      ?? null;
  const objectFit     = hotspot.meta?.objectFit     ?? 'cover';
  const bgStrokeColor = hotspot.meta?.bgStrokeColor ?? 'transparent';
  const bgStrokeWidth = (hotspot.meta?.bgStrokeWidth ?? 0) * canvasScale;
  const borderRadius  = (hotspot.meta?.borderRadius  ?? 0) * canvasScale;

  return (
    <Box
      ref={elemRef}
      onMouseDown={e => { e.stopPropagation(); onMouseDown?.(e); }}
      onClick={onClick}
      onContextMenu={!previewMode && onContextMenu ? e => { e.preventDefault(); e.stopPropagation(); onContextMenu(e); } : undefined}
      sx={{
        position: 'absolute',
        left: `${x}%`, top: `${y}%`,
        width: `${w}%`, height: `${h}%`,
        zIndex: 5 + (hotspot.order ?? 0),
        cursor: selected && !previewMode ? 'move' : previewMode ? 'default' : 'grab',
        outline: selected && !previewMode ? '2px solid #e67e22' : !previewMode ? '1px dashed rgba(230,126,34,0.4)' : 'none',
        outlineOffset: selected ? 2 : 0,
        boxShadow: selected && !previewMode ? '0 0 0 3px rgba(230,126,34,0.2)' : 'none',
        border: bgStrokeWidth > 0 ? `${bgStrokeWidth}px solid ${bgStrokeColor}` : 'none',
        borderRadius: `${borderRadius}px`,
        overflow: 'visible',
        pointerEvents: 'all',
        userSelect: 'none', WebkitUserSelect: 'none',
        boxSizing: 'border-box',
        background: imageUrl ? 'transparent' : 'rgba(16,117,186,0.20)',
        transition: 'outline 0.1s, box-shadow 0.1s',
      }}
    >
      {/* Resize handles */}
      {selected && !previewMode && IMAGE_HANDLES.map(handle => (
        <Box
          key={handle}
          onMouseDown={e => { e.stopPropagation(); onHandleMouseDown?.(e, handle); }}
          sx={{
            position: 'absolute', width: 8, height: 8,
            borderRadius: '2px', background: '#fff',
            border: '1.5px solid #e67e22',
            cursor: IMAGE_HANDLE_CURSORS[handle],
            zIndex: 30, ...IMAGE_HANDLE_POS[handle],
          }}
        />
      ))}

      {/* Image or placeholder */}
      <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: `${borderRadius}px` }}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            sx={{ width: '100%', height: '100%', objectFit, display: 'block', pointerEvents: 'none' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, cursor: 'pointer' }}>
            <ImageOutlined sx={{ fontSize: 48, color: 'rgba(16,117,186,0.70)' }} />
            <Box sx={{ fontSize: '0.85rem', color: 'rgba(16,117,186,0.70)', fontWeight: 700, letterSpacing: '0.02em' }}>Select image</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}


// Re-export handle constants used externally
export { ExhibitorGridClipDef };
